import type { ReactNode } from "react";
import { ReasonDialog, ErrorSurface } from "@steelyard/theme";
import { formatMoney } from "../../lib/format";
import type { DemotionRefusal } from "../../lib/services/customers";

/** Ported from apps/admin/components/customer-demote-form.tsx. A refusal names the balance, never "not permitted". */
export function DemotionRefused({ refusal }: { refusal: DemotionRefusal }) {
  return (
    <ErrorSurface
      error={{
        title: refusal.title,
        status: 409,
        detail: refusal.outstanding === null ? undefined : `${formatMoney(refusal.outstanding)} is still outstanding on this account.`,
      }}
      action="demote this customer"
    >
      {refusal.openCreditOrders.length > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="font-medium">
            {refusal.openCreditOrders.length === 1 ? "One credit order is still open:" : `${refusal.openCreditOrders.length} credit orders are still open:`}
          </p>
          <ul className="list-disc pl-5 font-mono text-xs">
            {refusal.openCreditOrders.map((reference) => (
              <li key={reference}>{reference}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </ErrorSurface>
  );
}

export function DemoteCustomerForm({
  open,
  onSubmit,
  customerName,
  defaultTierId,
  outstanding,
  cancel,
  error,
}: {
  open: boolean;
  onSubmit: (formData: FormData) => void | Promise<void>;
  customerName: string;
  defaultTierId: string;
  outstanding?: string;
  cancel: ReactNode;
  /** The failed attempt's error, if any — rendered inline the same way FieldError formats one. */
  error?: unknown;
}) {
  const errorMessage = error instanceof Error ? error.message : typeof error === "string" ? error : error ? String(error) : undefined;

  return (
    <ReasonDialog
      open={open}
      title={`Demote ${customerName} to the Default Tier`}
      consequence={
        <>
          They move to piece prices and pay before dispatch from their next load. Their credit limit and payment
          term are removed. This is audit-logged and cannot be undone from this screen — restoring terms means
          assigning them again.
        </>
      }
      action={onSubmit}
      confirmLabel="Demote to Default Tier"
      cancel={cancel}
      reasonLabel="Why is this customer being demoted?"
      reasonHint="Recorded in the audit log. Infra Junction refuses a demotion with no reason."
      error={errorMessage}
    >
      <input type="hidden" name="defaultTierId" value={defaultTierId} />
      {outstanding ? (
        <p className="text-muted-foreground text-sm">
          {customerName} currently owes <strong>{outstanding}</strong>. Infra Junction refuses a demotion while a
          balance is outstanding (BR-09), so this will be declined until it is settled.
        </p>
      ) : null}
    </ReasonDialog>
  );
}
